import { useState } from 'react';
import { Search, Loader2, HeartPulse } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { BasePerson } from '@/dtos/Persons/index';

interface PersonSearchProps {
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  onSearch: () => void;
  searchResults: BasePerson[];
  isLoading: boolean;
  isSearching: boolean;
  onSelectPerson: (person: BasePerson) => void;
  onCreateNew: () => void;
}

export default function PersonSearch({
  searchTerm,
  onSearchTermChange,
  onSearch,
  searchResults,
  isLoading,
  isSearching,
  onSelectPerson,
  onCreateNew,
}: PersonSearchProps) {
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          placeholder="Căutați după ID intern, nume, CNP, email sau telefon..."
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch()}
        />
        <Button onClick={onSearch} disabled={isSearching || isLoading}>
          {isSearching || isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID Intern</TableHead>
                <TableHead>Nume</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Profil Sănătate</TableHead>
                <TableHead className="w-[100px]">Acțiune</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {searchResults.map((person) => (
                <TableRow key={person.id}>
                  <TableCell className="font-mono text-sm">
                    {person.personInternalId || '-'}
                  </TableCell>
                  <TableCell>
                    {person.firstName} {person.lastName}
                  </TableCell>
                  <TableCell>{person.email || '-'}</TableCell>
                  <TableCell>
                    {person.healthProfile ? (
                      <Badge variant="default" className="gap-1">
                        <HeartPulse className="h-3 w-3" />
                        Da
                      </Badge>
                    ) : (
                      <Badge variant="outline">Nu</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" onClick={() => onSelectPerson(person)}>
                      Selectează
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {searchTerm && searchResults.length === 0 && !isSearching && !isLoading && (
        <div className="text-center py-4 text-muted-foreground">
          <p>Nu s-au găsit rezultate pentru "{searchTerm}"</p>
          <Button variant="link" onClick={onCreateNew} className="mt-2">
            Creează un pacient nou
          </Button>
        </div>
      )}
    </div>
  );
}
